import {
  GitHubAppKindSchema,
  GitIntegrationPlatformSchema,
  PrSyncModeSchema,
  type GitHubAppKindType,
  type GitIntegrationPlatformType,
  type PrSyncModeType,
} from '@terra/shared-schemas-git';

export type SourceCodeIntegrationDetails = {
  id: string;
  name?: string | null;
  platform: GitIntegrationPlatformType;
  baseUrl?: string | null;
  type?: string | null;
  gitHubAppKind?: GitHubAppKindType | null;
  prSyncMode?: PrSyncModeType | null;
  webhookSecretEnc?: string | null;
  twingateNetworkId?: string | null;
  internalAccessNetworkId?: string | null;
};

export type SourceCodeRepositoryRow = {
  id: string | number;
  name: string;
  targetBranch?: string | null;
  targetPath?: string | null;
};

export const SOURCE_CODE_PROVIDERS = {
  GITHUB: 'github',
  GITLAB: 'gitlab',
  AZURE_DEVOPS: 'azureDevOps',
} as const;

export type SourceCodeProvider =
  (typeof SOURCE_CODE_PROVIDERS)[keyof typeof SOURCE_CODE_PROVIDERS];

export const SOURCE_CODE_INFO_KINDS = {
  MOUNTED: 'mounted',
  ZIP: 'zip',
  APP: 'app',
  TOKEN: 'token',
} as const;

export type SourceCodeInfoKind =
  (typeof SOURCE_CODE_INFO_KINDS)[keyof typeof SOURCE_CODE_INFO_KINDS];

export const SOURCE_CODE_MENU_ACTIONS = {
  EDIT_NAME: 'edit-name',
  EDIT_REPOSITORIES: 'edit-repositories',
  TRANSFORM: 'transform',
  DISCONNECT: 'disconnect',
} as const;

export type SourceCodeMenuAction =
  (typeof SOURCE_CODE_MENU_ACTIONS)[keyof typeof SOURCE_CODE_MENU_ACTIONS];

export type SourceCodeProviderTitles = Record<SourceCodeProvider, string> & {
  mounted: string;
};

export type WebhookSetupLabel =
  | { key: 'webhookPollingType'; intervalHours: number }
  | { key: 'webhookPollingLabel' | 'webhookManual' | 'webhookAutomatic' };

export const isZipUploadIntegration = (
  platform: GitIntegrationPlatformType,
): boolean => platform === GitIntegrationPlatformSchema.enum.ZIP_UPLOAD;

export const isInternalDeploymentIntegration = (
  integration: Pick<
    SourceCodeIntegrationDetails,
    'twingateNetworkId' | 'internalAccessNetworkId'
  >,
): boolean =>
  integration.twingateNetworkId != null ||
  integration.internalAccessNetworkId != null;

export const resolveSourceCodeProvider = (
  platform: GitIntegrationPlatformType,
): SourceCodeProvider | null => {
  switch (platform) {
    case GitIntegrationPlatformSchema.enum.GITHUB:
    case GitIntegrationPlatformSchema.enum.GITHUB_ENTERPRISE:
      return SOURCE_CODE_PROVIDERS.GITHUB;
    case GitIntegrationPlatformSchema.enum.GITLAB:
    case GitIntegrationPlatformSchema.enum.GITLAB_SELF_HOSTED:
      return SOURCE_CODE_PROVIDERS.GITLAB;
    case GitIntegrationPlatformSchema.enum.AZURE_DEVOPS:
      return SOURCE_CODE_PROVIDERS.AZURE_DEVOPS;
    default:
      return null;
  }
};

const toHostname = (url: string): string => {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
};

export const resolveSourceCodeIntegrationName = ({
  integration,
  isMounted,
  titles,
}: {
  integration: Pick<
    SourceCodeIntegrationDetails,
    'name' | 'platform' | 'baseUrl'
  >;
  isMounted: boolean;
  titles: SourceCodeProviderTitles;
}): string => {
  if (isMounted) {
    return titles.mounted;
  }

  if (integration.name) {
    return integration.name;
  }

  const provider = resolveSourceCodeProvider(integration.platform);

  if (provider === SOURCE_CODE_PROVIDERS.AZURE_DEVOPS) {
    return (
      integration.baseUrl?.match(/dev\.azure\.com\/([^/]+)/)?.[1] ??
      titles[provider]
    );
  }

  if (provider) {
    return integration.baseUrl
      ? toHostname(integration.baseUrl)
      : titles[provider];
  }

  return integration.platform;
};

export const resolvePermissionTypeKey = (
  integration: Pick<SourceCodeIntegrationDetails, 'platform' | 'gitHubAppKind'>,
): 'permissionFull' | 'permissionBasic' | null => {
  if (integration.platform !== GitIntegrationPlatformSchema.enum.GITHUB) {
    return null;
  }

  if (integration.gitHubAppKind === GitHubAppKindSchema.enum.FULL) {
    return 'permissionFull';
  }

  if (integration.gitHubAppKind === GitHubAppKindSchema.enum.BASIC) {
    return 'permissionBasic';
  }

  return null;
};

export const resolveWebhookSetupLabel = ({
  prSyncMode,
  pollingIntervalHours,
  hasWebhookSecret,
}: {
  prSyncMode: PrSyncModeType | null | undefined;
  pollingIntervalHours: number | null | undefined;
  hasWebhookSecret: boolean;
}): WebhookSetupLabel => {
  if (prSyncMode === PrSyncModeSchema.enum.POLLING) {
    return pollingIntervalHours !== undefined && pollingIntervalHours !== null
      ? { key: 'webhookPollingType', intervalHours: pollingIntervalHours }
      : { key: 'webhookPollingLabel' };
  }

  if (prSyncMode === PrSyncModeSchema.enum.MANUAL_WEBHOOK) {
    return { key: 'webhookManual' };
  }

  if (prSyncMode === PrSyncModeSchema.enum.AUTO_WEBHOOK) {
    return { key: 'webhookAutomatic' };
  }

  return { key: hasWebhookSecret ? 'webhookManual' : 'webhookAutomatic' };
};

export const resolveSourceCodeInfoKind = ({
  integration,
  isMounted,
}: {
  integration: Pick<SourceCodeIntegrationDetails, 'platform' | 'type'>;
  isMounted: boolean;
}): SourceCodeInfoKind => {
  if (isMounted) {
    return SOURCE_CODE_INFO_KINDS.MOUNTED;
  }

  if (isZipUploadIntegration(integration.platform)) {
    return SOURCE_CODE_INFO_KINDS.ZIP;
  }

  return integration.type === 'APP'
    ? SOURCE_CODE_INFO_KINDS.APP
    : SOURCE_CODE_INFO_KINDS.TOKEN;
};

export const resolveSourceCodeMenuActions = ({
  infoKind,
  canEditName,
  canEditRepositories,
  canTransform,
  canDisconnect,
}: {
  infoKind: SourceCodeInfoKind;
  canEditName: boolean;
  canEditRepositories: boolean;
  canTransform: boolean;
  canDisconnect: boolean;
}): SourceCodeMenuAction[] => {
  const isMounted = infoKind === SOURCE_CODE_INFO_KINDS.MOUNTED;
  const isZipOnly = infoKind === SOURCE_CODE_INFO_KINDS.ZIP;
  const candidates: [SourceCodeMenuAction, boolean][] = [
    [SOURCE_CODE_MENU_ACTIONS.EDIT_NAME, canEditName && !isMounted],
    [
      SOURCE_CODE_MENU_ACTIONS.EDIT_REPOSITORIES,
      canEditRepositories && !isZipOnly,
    ],
    [SOURCE_CODE_MENU_ACTIONS.TRANSFORM, canTransform && isZipOnly],
    [SOURCE_CODE_MENU_ACTIONS.DISCONNECT, canDisconnect],
  ];

  return candidates
    .filter(([, isAvailable]) => isAvailable)
    .map(([action]) => action);
};
